import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FaCog,
    FaUser,
    FaBuilding,
    FaLock,
    FaStore,
    FaFileInvoice,
} from "react-icons/fa";

import {
    useSelector,
} from "react-redux";

import {
    selectTenant,
    selectTenantUser,
    selectIsTenantOwner,
    selectTenantRoleCode,
    selectTenantUserType,
} from "../../../../../store/tenantProfile/tenantProfileSlice";

import BasicSettings
    from "./BasicSettings";

import ProfileSettings
    from "./ProfileSettings";

import BusinessSettings
    from "./BusinessSettings";

import ChangePasswordSettings
    from "./ChangePasswordSettings";

import StorefrontSettings
    from "./StorefrontSettings";

import InvoiceTemplateSettings
    from "./InvoiceTemplateSettings";

import "./Settings.css";


const SETTINGS_TABS = {

    BASIC:
        "BASIC",

    PROFILE:
        "PROFILE",

    BUSINESS:
        "BUSINESS",

    STOREFRONT:
        "STOREFRONT",

    INVOICE_TEMPLATE:
        "INVOICE_TEMPLATE",

    PASSWORD:
        "PASSWORD",

};


const SettingsLayout =
    () => {

        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const tenant =
            useSelector(
                selectTenant
            );


        const user =
            useSelector(
                selectTenantUser
            );


        const isOwner =
            useSelector(
                selectIsTenantOwner
            );


        const roleCode =
            useSelector(
                selectTenantRoleCode
            );


        const userType =
            useSelector(
                selectTenantUserType
            );


        /*
        |--------------------------------------------------------------------------
        | SETTINGS ACCESS
        |--------------------------------------------------------------------------
        |
        | OWNER:
        | user.isOwner === true
        | OR user.userType === "OWNER"
        |
        | ADMIN:
        | role.code === "ADMIN"
        | OR user.userType === "ADMIN"
        |
        |--------------------------------------------------------------------------
        */

        const hasFullSettingsAccess =
            Boolean(
                isOwner
            ) ||
            userType ===
                "OWNER" ||
            userType ===
                "ADMIN" ||
            roleCode ===
                "OWNER" ||
            roleCode ===
                "ADMIN";


        /*
        |--------------------------------------------------------------------------
        | AVAILABLE TABS
        |--------------------------------------------------------------------------
        */

        const tabs =
            useMemo(
                () => {

                    /*
                    |--------------------------------------------------------------------------
                    | NON ADMIN / NON OWNER
                    |--------------------------------------------------------------------------
                    */

                    if (
                        !hasFullSettingsAccess
                    ) {

                        return [
                            {
                                key:
                                    SETTINGS_TABS.PROFILE,

                                title:
                                    "Profile",

                                icon:
                                    <FaUser />,
                            },

                            {
                                key:
                                    SETTINGS_TABS.PASSWORD,

                                title:
                                    "Change Password",

                                icon:
                                    <FaLock />,
                            },
                        ];

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | OWNER / ADMIN
                    |--------------------------------------------------------------------------
                    */

                    return [
                        {
                            key:
                                SETTINGS_TABS.BASIC,

                            title:
                                "Basic Settings",

                            icon:
                                <FaCog />,
                        },

                        {
                            key:
                                SETTINGS_TABS.PROFILE,

                            title:
                                "Profile",

                            icon:
                                <FaUser />,
                        },

                        {
                            key:
                                SETTINGS_TABS.BUSINESS,

                            title:
                                "Business Details",

                            icon:
                                <FaBuilding />,
                        },

                        {
                            key:
                                SETTINGS_TABS.STOREFRONT,

                            title:
                                "Storefront",

                            icon:
                                <FaStore />,
                        },

                        {
                            key:
                                SETTINGS_TABS.INVOICE_TEMPLATE,

                            title:
                                "Invoice Template",

                            icon:
                                <FaFileInvoice />,
                        },

                        {
                            key:
                                SETTINGS_TABS.PASSWORD,

                            title:
                                "Change Password",

                            icon:
                                <FaLock />,
                        },
                    ];

                },
                [
                    hasFullSettingsAccess,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | ACTIVE TAB
        |--------------------------------------------------------------------------
        */

        const [
            activeTab,
            setActiveTab,
        ] =
            useState(
                SETTINGS_TABS.PROFILE
            );


        /*
        |--------------------------------------------------------------------------
        | SET DEFAULT TAB AFTER ROLE IS KNOWN
        |--------------------------------------------------------------------------
        |
        | This avoids the previous issue where React initialized before
        | tenant profile data had finished loading.
        |
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                if (
                    hasFullSettingsAccess
                ) {

                    /*
                    | Keep current valid tab.
                    | If user is currently on Profile after initial load,
                    | switch owner/admin to Basic Settings.
                    */

                    if (
                        activeTab ===
                        SETTINGS_TABS.PROFILE
                    ) {

                        setActiveTab(
                            SETTINGS_TABS.BASIC
                        );

                    }

                    return;

                }


                /*
                |--------------------------------------------------------------------------
                | EMPLOYEE / OTHER ROLE
                |--------------------------------------------------------------------------
                */

                const allowedTabs = [
                    SETTINGS_TABS.PROFILE,
                    SETTINGS_TABS.PASSWORD,
                ];


                if (
                    !allowedTabs.includes(
                        activeTab
                    )
                ) {

                    setActiveTab(
                        SETTINGS_TABS.PROFILE
                    );

                }

            },
            [
                hasFullSettingsAccess,
                activeTab,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | RENDER CONTENT
        |--------------------------------------------------------------------------
        */

        const renderContent =
            () => {

                /*
                |--------------------------------------------------------------------------
                | EMPLOYEE GUARD
                |--------------------------------------------------------------------------
                */

                if (
                    !hasFullSettingsAccess
                ) {

                    if (
                        activeTab ===
                        SETTINGS_TABS.PASSWORD
                    ) {

                        return (
                            <ChangePasswordSettings />
                        );

                    }


                    return (
                        <ProfileSettings />
                    );

                }


                /*
                |--------------------------------------------------------------------------
                | OWNER / ADMIN
                |--------------------------------------------------------------------------
                */

                switch (
                    activeTab
                ) {

                    case SETTINGS_TABS.PROFILE:

                        return (
                            <ProfileSettings />
                        );


                    case SETTINGS_TABS.BUSINESS:

                        return (
                            <BusinessSettings />
                        );


                    case SETTINGS_TABS.STOREFRONT:

                        return (
                            <StorefrontSettings />
                        );


                    case SETTINGS_TABS.INVOICE_TEMPLATE:

                        return (
                            <InvoiceTemplateSettings />
                        );


                    case SETTINGS_TABS.PASSWORD:

                        return (
                            <ChangePasswordSettings />
                        );


                    case SETTINGS_TABS.BASIC:

                    default:

                        return (
                            <BasicSettings />
                        );

                }

            };


        return (

            <div className="settings-page">

                {/* ============================================================
                    PAGE HEADER
                ============================================================ */}

                <div className="settings-page__header">

                    <div>

                        <span className="settings-page__eyebrow">

                            SETTINGS

                        </span>


                        <h1>

                            Account & Business Settings

                        </h1>


                        <p>

                            {
                                hasFullSettingsAccess
                                    ?
                                    "Manage your profile, business details, preferences and account security."
                                    :
                                    "Manage your profile and account security."
                            }

                        </p>

                    </div>

                </div>


                {/* ============================================================
                    ACCOUNT SUMMARY
                ============================================================ */}

                <div className="settings-summary">

                    <div className="settings-summary__avatar">

                        {
                            String(
                                user?.name ||
                                tenant?.name ||
                                "C"
                            )
                                .charAt(
                                    0
                                )
                                .toUpperCase()
                        }

                    </div>


                    <div className="settings-summary__identity">

                        <span>

                            Current Business

                        </span>


                        <strong>

                            {
                                tenant?.name ||
                                "Business"
                            }

                        </strong>

                    </div>


                    <div className="settings-summary__meta">

                        <div>

                            <span>

                                User

                            </span>


                            <strong>

                                {
                                    user?.name ||
                                    "-"
                                }

                            </strong>

                        </div>


                        <div>

                            <span>

                                Business Code

                            </span>


                            <strong>

                                {
                                    tenant?.code ||
                                    "-"
                                }

                            </strong>

                        </div>


                        <div>

                            <span>

                                Status

                            </span>


                            <strong>

                                {
                                    tenant?.status ||
                                    "-"
                                }

                            </strong>

                        </div>

                    </div>

                </div>


                {/* ============================================================
                    SETTINGS CARD
                ============================================================ */}

                <div className="settings-card">

                    <div className="settings-tabs">

                        {
                            tabs.map(
                                (
                                    tab
                                ) => (

                                    <button
                                        key={
                                            tab.key
                                        }

                                        type="button"

                                        className={[
                                            "settings-tab",

                                            activeTab ===
                                                tab.key
                                                ?
                                                "active"
                                                :
                                                "",
                                        ]
                                            .filter(
                                                Boolean
                                            )
                                            .join(
                                                " "
                                            )}

                                        onClick={
                                            () =>
                                                setActiveTab(
                                                    tab.key
                                                )
                                        }
                                    >

                                        {tab.icon}

                                        <span>

                                            {tab.title}

                                        </span>

                                    </button>

                                )
                            )
                        }

                    </div>


                    <div className="settings-content">

                        {
                            renderContent()
                        }

                    </div>

                </div>

            </div>

        );

    };


export default SettingsLayout;